import { Injectable } from '@angular/core';
import moment from 'moment-timezone';
import { Moment } from 'moment-timezone';
import { Exception, ScheduleHours, DayHours } from '@shared/schedule/models';

// TODO: replace number[][] type with [number, number][]
@Injectable({providedIn: 'root'})
export class ScheduleService {
  /**
   * How it works:
   * 1. Generates intervals for for given, previous and next days. Previous day contains
   *    negative values for correct calculations.
   * 2. Merges these intervals, see description of mergeIntervals()
   * 3. Check if the given date and time inside any interval (respect service duration or
   *    not).
   *
   * @param hours
   * @param exceptions
   * @param {number} timestamp UTC time
   * @param {string} timezone Restaurant's timezone
   * @param {number} duration Respect service duration or not
   * @returns {boolean} is given date and time in schedule
   */
  check(hours: ScheduleHours, exceptions: Exception[], timestamp: number, timezone: string, duration = 0): boolean {
    return this.inIntervals(
      this.getIntervals(hours, exceptions, timestamp, timezone),
      timestamp,
      timestamp + duration,
    );
  }

  getIntervals(
    hours: ScheduleHours,
    exceptions: Exception[],
    timestamp: number,
    timezone: string,
    includeNextDay = true,
    isPublic = false,
  ) {
    const today = moment.tz(timestamp, timezone);
    const previousDay = moment.tz(timestamp, timezone).subtract(1, 'day');
    const nextDay = moment.tz(timestamp, timezone).add(1, 'day');

    const intervals = [
      ...this.generateIntervals(
        hours[previousDay.format('ddd')],
        this.getExceptions(exceptions, previousDay.format('D-M-YYYY'), timezone),
        previousDay,
        timezone,
        isPublic,
      ),
      ...this.generateIntervals(
        hours[today.format('ddd')],
        this.getExceptions(exceptions, today.format('D-M-YYYY'), timezone),
        today,
        timezone,
        isPublic,
      ),
    ];
    if (includeNextDay) {
      intervals.push(...this.generateIntervals(
        hours[nextDay.format('ddd')],
        this.getExceptions(exceptions, nextDay.format('D-M-YYYY'), timezone),
        nextDay,
        timezone,
        isPublic,
      ));
    }

    return this.mergeIntervals(intervals);
  }

  /**
   * Generates working intervals from schedule for given date and with given offset by
   * the following priority:
   * - any closed exception - empty intervals, closed the whole day
   * - any other exceptions - returns interval (since time to time) for these exceptions
   * - regular schedule - return interval according regular schedule
   *
   * Examples:
   * closed: []
   * exception 8AM-11PM: [28800000, 82800000]
   * regular schedule 9AM-1PM and 2PM-7PM: [[36000000, 46800000], [50400000, 68400000]]
   * regular schedule 9AM-6PM: [[36000000, 64800000]]
   *
   * @param {DayHours} dayHours Regular hours for any weekday
   * @param {array} exceptions Schedule exceptions
   * @param {Moment} date
   * @param {string} timezone Is added start and end of intervals
   * @param {boolean} isPublic
   * @returns {[]|*[]} array of intervals
   */
  generateIntervals(
    dayHours: DayHours,
    exceptions: Exception[],
    date: Moment,
    timezone: string,
    isPublic = false,
  ): number[][] {
    const dateString = date.format('D-M-YYYY');
    const intervals: number[][] = [];

    for (const exception of exceptions) {
      if ((exception.hours[0][0] === '' && exception.publicDisabled) || (isPublic && exception.publicDisabled)) {
        continue;
      }

      intervals.push(...exception.hours.map(hours => hours.map((hour, index) => {
        const addDay = exception.nextDay && index > 0;

        return this.timeToTimestamp(Number(hour), dateString, timezone, addDay);
      })));
    }

    // No exceptions for this day
    if (intervals.length) {
      return intervals;
    }

    let closedException = null;
    let openedException = null;
    let publicDisabledException = null;

    for (const exception of exceptions) {
      if (exception.closed) {
        closedException = exception;
      }

      if (!exception.closed) {
        openedException = exception;
      }

      if (isPublic && exception.publicDisabled) {
        publicDisabledException = exception;
      }
    }

    const isClosed = (dayHours.closed || !!closedException) && !openedException;
    const publicDisabled = isPublic && (dayHours.publicDisabled || !!publicDisabledException);
    if (isClosed || publicDisabled) {
      return [];
    }

    const [startOfDayTimestamp, endOfDayTimestamp] = this.getStartAndEndOfDay(date);
    intervals.push(...dayHours.hours.map(hours => hours.map((hour, index) => {
      if (hour === '') {
        return index === 0 ? startOfDayTimestamp : endOfDayTimestamp;
      }

      return this.timeToTimestamp(Number(hour), dateString, timezone, dayHours.nextDay && index > 0);
    })));

    return intervals;
  }

  /**
   * Accepts array of intervals in milliseconds (now we keep them in milliseconds) and
   * merge them when it is possible.
   *
   * Example (given in time but actually in milliseconds, negative means previous day,
   * time greater than 24 hours means next day):
   * Input: [[-11:00, -16:00], [-14:00, -18:00], [9:00,25:00], [25:00, 46:00]];
   * Output: [[-11:00, -18:00], [9:00, 46:00]]
   *
   * @param {array} intervals number[][]
   * @returns {[]|*} array of intervals
   */
  mergeIntervals(intervals: number[][]): number[][] {
    if (intervals.length < 2) {
      return intervals;
    }

    intervals.sort((a, b) => a[0] - b[0]);
    const _intervals: number[][] = [];
    _intervals.push(intervals[0]);
    let index = 0;
    let nextElementIndex = 1;
    while (nextElementIndex < intervals.length) {
      const [, b1] = _intervals[index];
      const [a2, b2] = intervals[nextElementIndex];
      if (b1 < a2) {
        _intervals.push([a2, b2]);
        index++;
        nextElementIndex++;
        continue;
      }

      if (b1 < b2) {
        _intervals[index][1] = b2;
      }
      nextElementIndex++;
    }

    return _intervals;
  }

  /**
   * Returns schedule exceptions for a give date
   * @param exceptions
   * @param date
   * @param timezone
   */
  getExceptions(exceptions: Exception[], date: string, timezone: string): Exception[] {
    if (exceptions?.length && date) {
      return exceptions.filter(exception => this.parseDate(exception.date, timezone) === date);
    }

    return [];
  }

  /**
   * Accepts timestamp or date string and returns date in format 'D-M-YYYY' or passed
   * timestamp without changes if timestamp is non-numeric string.
   * Since of the frontend changes it is possible some closed exceptions can have date in
   * format D-M-YYYY.
   *
   * @param timestamp {number|string}
   * @param timezone {string} timezone
   */
  parseDate(timestamp: number | string, timezone: string): string {
    if (typeof timestamp === 'number' || !isNaN(Number(timestamp))) {
      return moment(Number(timestamp)).tz(timezone).format('D-M-YYYY');
    }

    return timestamp; // It is a date in format D-M-YYYY, return it as is
  }

  /**
   *
   * @param time {number} time to milliseconds
   * @param timezone {string} timezone
   */
  timeToTimeString(time: number, timezone: string): string {
    return moment.tz(time, timezone).format('hh:mm A');
  }

  /**
   * Converts time to timestamp for a given date and timezone
   *
   * @param value {number} hours in milliseconds
   * @param date {string} date in format 'D-M-YYYY'
   * @param timezone {string}
   * @param {boolean} addDay
   * @return {number}
   */
  timeToTimestamp(value: number, date: string, timezone: string, addDay = false): number {
    const _date = moment.tz(`${date} ${this.timeToTimeString(value, timezone)}`, 'D-M-YYYY hh:mm A', timezone);
    if (addDay) {
      _date.add(1, 'day');
    }

    return _date.valueOf();
  }

  inIntervals(intervals: number[][], start: number, end: number): boolean {
    for (const interval of intervals) {
      if (start >= interval[0] && end <= interval[1] && start < interval[1]) {
        return true;
      }
    }

    return false;
  }

  getStartAndEndOfDay(date: Moment) {
    const startOfDay = date.clone().startOf('day');

    return [
      startOfDay.valueOf(),
      startOfDay.clone().add(1, 'day').valueOf(),
    ];
  }
}
